import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FeedbackSelector from '../FeedbackSelector';
import {
  QuizSlideData,
  Theme,
  slideTypographyStyle,
} from '../slideTypes';
import {
  springs,
  staggerContainer,
} from '../../../ui/slide-animations';
import { fireCorrect } from '../../../../hooks/use-confetti';
import { WrappingInput } from '../shared/WrappingInput';

export function QuizSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onQuizAnswered,
}: {
  data: QuizSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: QuizSlideData) => void;
  onQuizAnswered?: (result: { selectedOptionId: string; isCorrect: boolean }) => void;
}) {
  const addOption = (afterIndex?: number) => {
    const newOption = { id: crypto.randomUUID(), text: '', isCorrect: false };
    if (afterIndex !== undefined) {
      const newOptions = [...data.options];
      newOptions.splice(afterIndex + 1, 0, newOption);
      onUpdate({ ...data, options: newOptions });
    } else {
      onUpdate({ ...data, options: [...data.options, newOption] });
    }
    // Focus new option after render
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-quiz-option]');
      const targetIndex = afterIndex !== undefined ? afterIndex + 1 : inputs.length - 1;
      inputs[targetIndex]?.focus();
    }, 50);
  };

  const updateOption = (id: string, updates: Partial<{ text: string; isCorrect: boolean; feedback: string }>) => {
    onUpdate({
      ...data,
      options: data.options.map((opt) => (opt.id === id ? { ...opt, ...updates } : opt)),
    });
  };

  const removeOption = (id: string) => {
    const index = data.options.findIndex(o => o.id === id);
    onUpdate({ ...data, options: data.options.filter((opt) => opt.id !== id) });
    // Focus previous option
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-quiz-option]');
      const targetIndex = Math.max(0, index - 1);
      inputs[targetIndex]?.focus();
    }, 50);
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, optionId: string, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption(index);
    } else if (e.key === 'Backspace' && data.options[index].text === '' && data.options.length > 1) {
      e.preventDefault();
      removeOption(optionId);
    }
  };

  // Viewing mode state
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<'idle' | 'selected' | 'correct' | 'incorrect'>('idle');
  const [shakeOptionId, setShakeOptionId] = useState<string | null>(null);
  const correctOptionRef = useRef<HTMLButtonElement>(null);

  const handleViewingSelect = (optionId: string) => {
    if (answerState === 'correct' || answerState === 'incorrect') return;
    setSelectedOption(optionId);
    setAnswerState('selected');
  };

  const handleCheckAnswer = () => {
    if (!selectedOption) return;
    const selected = data.options.find(o => o.id === selectedOption);
    // Gate opens on any answer attempt; also persists the answer so it
    // lands in activity_responses (and is visible in reports / timeline).
    onQuizAnswered?.({ selectedOptionId: selectedOption, isCorrect: !!selected?.isCorrect });
    if (selected?.isCorrect) {
      setAnswerState('correct');
      if (correctOptionRef.current) {
        fireCorrect();
      } else {
        fireCorrect();
      }
    } else {
      setAnswerState('incorrect');
      setShakeOptionId(selectedOption);
      setTimeout(() => setShakeOptionId(null), 500);
    }
  };

  const handleTryAgain = () => {
    setSelectedOption(null);
    setAnswerState('idle');
    setShakeOptionId(null);
  };

  // Editing mode
  if (isEditing) {
    return (
      <div className="h-full p-8 md:p-16 flex flex-col justify-center">
        <WrappingInput
          value={data.question}
          onChange={(question) => onUpdate({ ...data, question })}
          placeholder="Enter your question..."
          className="w-full mb-8 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          data-slide-level="heading"
          style={{
            ...slideTypographyStyle(theme, 'heading'),
            color: theme.colors.text,
          }}
        />
        <div className="space-y-3">
          {data.options.map((option, index) => (
            <div key={option.id}>
              <div className="flex items-center gap-3 group">
                <label className="relative flex items-center gap-1 cursor-pointer group/correct shrink-0" title="Mark as correct answer">
                  <input
                    type="checkbox"
                    checked={option.isCorrect}
                    onChange={(e) => updateOption(option.id, { isCorrect: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    option.isCorrect
                      ? 'border-green-500 bg-green-500'
                      : 'border-gray-300 hover:border-green-400'
                  }`}>
                    {option.isCorrect && (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-xs font-medium select-none ${
                    option.isCorrect ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {option.isCorrect ? '✓ Correct' : 'Correct?'}
                  </span>
                </label>
                <input
                  type="text"
                  data-quiz-option
                  value={option.text}
                  onChange={(e) => updateOption(option.id, { text: e.target.value })}
                  onKeyDown={(e) => handleOptionKeyDown(e, option.id, index)}
                  placeholder={`Option ${index + 1}... (Enter for new, Backspace to remove empty)`}
                  className="flex-1 px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderColor: option.isCorrect ? theme.colors.primary : 'transparent',
                    color: theme.colors.text,
                  }}
                />
                <button
                  onClick={() => removeOption(option.id)}
                  className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                >
                  ×
                </button>
              </div>
              {!option.isCorrect && (
                <div className="ml-[7.5rem] mr-8 mt-1">
                  <FeedbackSelector
                    value={option.feedback || ''}
                    onChange={(fb) => updateOption(option.id, { feedback: fb })}
                    theme={theme}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {data.options.length > 0 && (
          <button
            onClick={() => addOption()}
            className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            + Add option
          </button>
        )}
        {data.options.length === 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-sm text-gray-500 mb-2">No options yet</p>
            <button
              onClick={() => addOption()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Option
            </button>
          </div>
        )}
      </div>
    );
  }

  // Viewing mode with micro-interactions
  return (
    <div className="h-full flex flex-col justify-center items-center" style={{ padding: theme.spacing.padded }}>
      <div className="slide-content-narrow">
      <motion.h2
        className="mb-8"
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.gentle as object}
        data-slide-level="heading"
        style={{
          ...slideTypographyStyle(theme, 'heading'),
          color: theme.colors.text,
        }}
      >
        {data.question}
      </motion.h2>

      <motion.div
        className="space-y-4"
        variants={staggerContainer(0.08)}
        initial="hidden"
        animate="visible"
      >
        {data.options.map((option) => {
          const isSelected = selectedOption === option.id;
          const isCorrectOption = option.isCorrect;
          const isShaking = shakeOptionId === option.id;
          const isRevealed = answerState === 'correct' || answerState === 'incorrect';

          let borderColor = 'transparent';
          let bgColor = theme.colors.surface;
          let textColor = theme.colors.text;
          let opacity = 1;

          if (isRevealed) {
            if (isCorrectOption) {
              borderColor = theme.colors.feedback.correctBorder;
              bgColor = theme.colors.feedback.correctBg;
              textColor = theme.colors.feedback.correctText;
            } else if (isSelected && !isCorrectOption) {
              borderColor = theme.colors.feedback.incorrectBorder;
              bgColor = theme.colors.feedback.incorrectBg;
              textColor = theme.colors.feedback.incorrectText;
            } else {
              opacity = 0.5;
            }
          } else if (isSelected) {
            borderColor = theme.colors.primary;
            bgColor = theme.colors.surface;
          }

          return (
            <button
              key={option.id}
              ref={isCorrectOption ? correctOptionRef : undefined}
              onClick={() => handleViewingSelect(option.id)}
              className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all duration-150 cursor-pointer hover:shadow-[0_2px_8px_rgba(99,102,241,0.15)]"
              style={{
                ...slideTypographyStyle(theme, 'body', theme.fonts.body),
                backgroundColor: bgColor,
                borderColor,
                color: textColor,
                opacity,
              }}
              disabled={answerState === 'correct'}
            >
              {/* Radio dot */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                isSelected ? 'border-current' : 'border-gray-300'
              }`}>
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: isRevealed ? (isCorrectOption ? '#22c55e' : '#ef4444') : theme.colors.primary }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={springs.bouncy as object}
                    />
                  )}
                </AnimatePresence>
              </div>

              <span className="flex-1">{option.text}</span>

              {/* Result icons */}
              <AnimatePresence>
                {isRevealed && isCorrectOption && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy as object}
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: theme.colors.feedback.correctBorder }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
                {isRevealed && isSelected && !isCorrectOption && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy as object}
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: theme.colors.feedback.incorrectBorder }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </motion.svg>
                )}
              </AnimatePresence>
            </button>
          );
        })}
      </motion.div>

      {/* Check Answer / Try Again buttons */}
      <AnimatePresence mode="wait">
        {answerState === 'selected' && (
          <motion.div
            key="check"
            className="mt-6 flex justify-center"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={springs.gentle as object}
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleCheckAnswer}
              className="px-8 py-3 rounded-xl font-semibold text-white shadow-lg"
              style={{ backgroundColor: theme.colors.primary }}
            >
              Check Answer
            </motion.button>
          </motion.div>
        )}
        {answerState === 'incorrect' && (
          <motion.div
            key="tryagain"
            className="mt-6 flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={springs.gentle as object}
          >
            {selectedOption && data.options.find(o => o.id === selectedOption)?.feedback && (
              <p className="text-center font-medium text-sm" style={{ color: theme.colors.feedback.incorrectText }}>
                {data.options.find(o => o.id === selectedOption)!.feedback}
              </p>
            )}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleTryAgain}
              className="px-8 py-3 rounded-xl font-semibold border-2 transition-colors"
              style={{ borderColor: theme.colors.primary, color: theme.colors.primary }}
            >
              Try Again
            </motion.button>
          </motion.div>
        )}
        {answerState === 'correct' && (
          <motion.div
            key="correct-msg"
            className="mt-6 flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springs.bouncy as object}
          >
            <div className="flex items-center gap-2 text-green-600 font-semibold text-lg">
              <motion.svg
                className="w-6 h-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={springs.bouncy as object}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </motion.svg>
              Correct!
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}
